import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import { HTMLEditorQuill } from '../../../../../components/HTMLEditorQuill/HTMLEditorQuill';
import { Loader } from '../../../../../components/Loader/Loader';
import { getFileName } from '../../../../../helpers/file/file';
import { Attachment } from '../../../../../models/generalMessage';
import '../TournamentNotificationWizard.scss';
import { AppUser } from 'Src/views/App/App';

interface Props {
  recipientDescription?: string;
  user: AppUser;
  onEmailSubjectChange: (event) => void;
  onEmailBodyChange: (editorState: any) => void;
  onEmailBodyEmptyTextChange: (emptyText: string) => void;
  onPushTitleChange: (event) => void;
  onPushBodyChange: (event) => void;
  onEmailAttachmentChange: (event) => void;
  onRemoveAttachment: (key: string) => void;
  onCloseImageError: () => void;
  onIsEmailEditorChange: (event) => void;
  onIsPushEditorChange: (event) => void;
  isLoadingFile: boolean;

  isEmailEditorEnabled: boolean;
  isPushEditorEnabled: boolean;
  isImageError: boolean;
  isError: boolean;
  isEditable?: boolean;
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;
  messageAttachments: Attachment[];
}

export const TournamentCustomNotificationTextForm: FunctionComponent<Props> = props => {
  const [contentTab, setContentTab] = useState<'email' | 'push'>('email');

  const {
    onEmailSubjectChange,
    onEmailBodyChange,
    onPushTitleChange,
    onPushBodyChange,
    onEmailAttachmentChange,
    messageEmailSubject,
    messageEmailBody,
    messagePushTitle,
    messagePushBody,
    isImageError,
    isError,
    isEditable,
    isEmailEditorEnabled,
    isPushEditorEnabled,
    isLoadingFile
  } = props;

  const isDisabled = isEditable === false;
  const isAllEditorDisabled = !isPushEditorEnabled && !isEmailEditorEnabled;

  const renderAttachments = () => {
    const { messageAttachments } = props;
    const onRemoveAttachment = props.onRemoveAttachment;

    return (
      <ul className="tTournamentNotificationWizard__attachmentList">
        {messageAttachments.map((attachment, index) => {
          const fileName = attachment.fileName;
          const attachmentFileName = getFileName(fileName);
          const fileSize =
            typeof attachment.fileSize !== 'undefined' ? `${(attachment.fileSize / 1024).toFixed(1)} KB` : '';

          return (
            <li key={`attachment_${index}`} className="tTournamentNotificationWizard__attachmentItem">
              <span>
                📎 {attachmentFileName}
                {fileSize ? ` (${fileSize})` : ''}
              </span>

              <button
                type="button"
                className="close ml-2"
                disabled={isImageError || isError}
                onClick={() => onRemoveAttachment(attachment.externalFileId)}
              >
                <span>&times;</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <div className="tTournamentNotificationWizard__contentTopbar">
        <div className="tTournamentNotificationWizard__channelSwitches" aria-label="Delivery channels">
          <div
            className={`tTournamentNotificationWizard__channel ${isEmailEditorEnabled ? 'is-on' : ''} ${
              contentTab === 'email' ? 'is-active' : ''
            }`}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-pressed={isEmailEditorEnabled}
            aria-disabled={isDisabled}
            onClick={() => {
              if (!isEmailEditorEnabled) props.onIsEmailEditorChange({ target: { checked: true } });
              setContentTab('email');
            }}
            onKeyDown={event => {
              if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                if (!isEmailEditorEnabled) props.onIsEmailEditorChange({ target: { checked: true } });
                setContentTab('email');
              }
            }}
          >
            <span className="tTournamentNotificationWizard__channelIcon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m4 7 8 6 8-6" />
              </svg>
            </span>
            <span>Email</span>
            {isEmailEditorEnabled ? (
              <span
                className="tTournamentNotificationWizard__channelCheck"
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-label="Disable Email"
                onClick={event => {
                  event.stopPropagation();
                  if (!isDisabled) {
                    props.onIsEmailEditorChange({ target: { checked: false } });
                    if (isPushEditorEnabled) setContentTab('push');
                  }
                }}
                onKeyDown={event => {
                  if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    event.stopPropagation();
                    props.onIsEmailEditorChange({ target: { checked: false } });
                    if (isPushEditorEnabled) setContentTab('push');
                  }
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m6 12 4 4 8-9" />
                </svg>
              </span>
            ) : (
              <span className="tTournamentNotificationWizard__channelAdd">+ enable</span>
            )}
          </div>
          <div
            className={`tTournamentNotificationWizard__channel ${isPushEditorEnabled ? 'is-on' : ''} ${
              contentTab === 'push' ? 'is-active' : ''
            }`}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-pressed={isPushEditorEnabled}
            aria-disabled={isDisabled}
            onClick={() => {
              if (!isPushEditorEnabled) props.onIsPushEditorChange({ target: { checked: true } });
              setContentTab('push');
            }}
            onKeyDown={event => {
              if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                if (!isPushEditorEnabled) props.onIsPushEditorChange({ target: { checked: true } });
                setContentTab('push');
              }
            }}
          >
            <span className="tTournamentNotificationWizard__channelIcon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="7" y="2.5" width="10" height="19" rx="2" />
                <path d="M10 5h4M11 18.5h2" />
              </svg>
            </span>
            <span>Push</span>
            {isPushEditorEnabled ? (
              <span
                className="tTournamentNotificationWizard__channelCheck"
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-label="Disable Push"
                onClick={event => {
                  event.stopPropagation();
                  if (!isDisabled) {
                    props.onIsPushEditorChange({ target: { checked: false } });
                    if (isEmailEditorEnabled) setContentTab('email');
                  }
                }}
                onKeyDown={event => {
                  if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    event.stopPropagation();
                    props.onIsPushEditorChange({ target: { checked: false } });
                    if (isEmailEditorEnabled) setContentTab('email');
                  }
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m6 12 4 4 8-9" />
                </svg>
              </span>
            ) : (
              <span className="tTournamentNotificationWizard__channelAdd">+ enable</span>
            )}
          </div>
        </div>
        <span className="tTournamentNotificationWizard__audienceHint">
          {props.recipientDescription || 'Recipients were selected on the previous screen'}
        </span>
      </div>

      <div className="tTournamentNotificationWizard__composeCard">
        <div className="tab-content tTournamentNotificationWizard__tabContent">
          <div className={`tab-pane fade ${contentTab === 'email' ? 'show active' : ''}`} role="tabpanel">
            <div className="form-group tTournamentNotificationWizard__field">
              <label htmlFor="tournamentNotificationEmailSubject">Email subject</label>
              <input
                id="tournamentNotificationEmailSubject"
                name="tournamentNotificationEmailSubject"
                type="text"
                className="form-control"
                onChange={onEmailSubjectChange}
                value={messageEmailSubject}
                autoComplete="new-password"
                data-1p-ignore="true"
                data-lpignore="true"
                disabled={!isEmailEditorEnabled || isDisabled}
              />
            </div>

            <div className="form-group eMessageTextEditor tTournamentNotificationWizard__field">
              <label>Email message</label>
              <HTMLEditorQuill
                user={props.user}
                onEmailBodyChange={onEmailBodyChange}
                value={messageEmailBody}
                isEnabled={isEmailEditorEnabled}
              />
            </div>

            {!isDisabled && (
              <div className="form-group tTournamentNotificationWizard__field">
                <label>Attachments</label>
                <label className="tTournamentNotificationWizard__dropzone" htmlFor="attachment">
                  <span aria-hidden="true">↑</span> Add an attachment
                  <input
                    type="file"
                    id="attachment"
                    onChange={onEmailAttachmentChange}
                    disabled={isImageError || isError || isAllEditorDisabled}
                  />
                </label>

                {isLoadingFile && <Loader />}

                {isImageError ? (
                  <div className="alert-danger mt-2 p-2">
                    Error uploading
                    <button type="button" className="close" onClick={props.onCloseImageError}>
                      <span>&times;</span>
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {props.messageAttachments.length > 0 && renderAttachments()}
          </div>

          <div className={`tab-pane fade ${contentTab === 'push' ? 'show active' : ''}`} role="tabpanel">
            <div className="form-group tTournamentNotificationWizard__field">
              <label htmlFor="tournamentNotificationPushTitle">Push title</label>
              <input
                id="tournamentNotificationPushTitle"
                name="tournamentNotificationPushTitle"
                type="text"
                className="form-control"
                onChange={onPushTitleChange}
                value={messagePushTitle}
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                disabled={!isPushEditorEnabled}
              />
            </div>

            <div className="form-group tTournamentNotificationWizard__field">
              <label>Push message</label>
              <textarea
                rows={8}
                onChange={onPushBodyChange}
                value={messagePushBody}
                disabled={!isPushEditorEnabled}
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

TournamentCustomNotificationTextForm.displayName = 'TournamentCustomNotificationTextForm';
